import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

// Fix for default markers in Leaflet with webpack
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconRetinaUrl: markerRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// District boundary will be loaded dynamically from database

// Function to add district boundary overlay
const addDistrictBoundary = (map, districtBoundary) => {
  try {
    // Use district boundary from database if available, otherwise skip
    if (!districtBoundary || districtBoundary.type !== 'Feature') {
      return; // No boundary to display
    }

    // Create GeoJSON layer for district boundary
    const boundaryLayer = L.geoJSON(districtBoundary, {
      style: function(feature) {
        return {
          fillColor: feature.properties?.fillColor || '#FF6B35',
          fillOpacity: feature.properties?.fillOpacity || 0.1,
          color: feature.properties?.color || '#FF6B35',
          weight: feature.properties?.weight || 2,
          opacity: feature.properties?.opacity || 0.8
        };
      }
    });

    // Add layer control for toggling boundary
    const overlays = {
      "District Boundary": boundaryLayer
    };

    L.control.layers(null, overlays, {
      position: 'topright',
      collapsed: false
    }).addTo(map);

    // Add boundary by default
    boundaryLayer.addTo(map);

  } catch (error) {
    console.error('StudentMap: Error adding district boundary:', error);
  }
};
const StudentMap = () => {
  const { user } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapContainerReady, setMapContainerReady] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (user) {
      fetchStudentCoordinates();
      fetchDistrictData();
    }
  }, [user]);

  const initializeMap = useCallback(() => {
    if (typeof L === 'undefined') {
      console.error('Leaflet is not available');
      return;
    }

    try {

      // Use district map center or fallback to Missouri center
      const mapCenter = district?.map_center ?
        [district.map_center.lat, district.map_center.lng] :
        [37.9643, -91.8318]; // Center of Missouri

      // Initialize map centered on district
      const map = L.map(mapRef.current, {
        attributionControl: false, // Remove default Leaflet attribution to avoid any flags
        scrollWheelZoom: false // Disable zoom on scroll to prevent accidental zooming when scrolling past map
      }).setView(mapCenter, 10);

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
      }).addTo(map);

      // Add custom attribution with Leaflet credit but no flag
      L.control.attribution({
        position: 'bottomright',
        prefix: 'Powered by Leaflet' // Simple text attribution without flag
      }).addTo(map);

      // Add district boundary overlay
      addDistrictBoundary(map, district?.district_boundary);

      mapInstanceRef.current = map;
      setMapReady(true);

    } catch (error) {
      console.error('StudentMap: Error initializing map:', error);
    }
  }, [district]);

  const fetchStudentCoordinates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/students/district-coordinates');
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch student coordinates:', error);
      setError('Failed to load student map data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistrictData = async () => {
    try {
      const response = await axios.get('/api/districts/current');
      setDistrict(response.data);
    } catch (error) {
      console.error('Failed to fetch district data:', error);
      // Continue with default values if district data fails to load
      setDistrict({
        map_center: { lat: 37.9643, lng: -91.8318, address: 'Missouri, USA', city: 'Missouri', state: 'USA', zip: '' },
        district_boundary: null
      });
    }
  };

  useLayoutEffect(() => {
    if (!mapContainerReady || !mapRef.current || mapInstanceRef.current || !district) {
      return;
    }

    initializeMap();

    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (error) {
          console.warn('Error removing map during cleanup:', error);
        }
        mapInstanceRef.current = null;
        setMapReady(false);
      }
    };
  }, [mapContainerReady, district, initializeMap]);

  // Add markers when students data loads and map is ready
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) {
      return;
    }

    const map = mapInstanceRef.current;

    // Clear existing markers
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        map.removeLayer(layer);
      }
    });

    // Add markers for students with coordinates
    students.forEach((student) => {
      // Validate coordinates
      const lat = parseFloat(student.coordinates?.lat);
      const lng = parseFloat(student.coordinates?.lng);

      if (student.coordinates && !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        const marker = L.marker([lat, lng]).addTo(map);

        // Create popup with student info
        const popupContent = `
          <div style="min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #2c3e50;">${student.first_name} ${student.last_name}</h4>
            <p style="margin: 4px 0;"><strong>Grade:</strong> ${student.grade || 'N/A'}</p>
            <p style="margin: 4px 0;"><strong>Address:</strong> ${student.address || 'N/A'}</p>
          </div>
        `;

        marker.bindPopup(popupContent);
      }
    });

    // Ensure we're at the district-focused view
    const districtCenter = district?.map_center ?
      [district.map_center.lat, district.map_center.lng] :
      [37.9643, -91.8318]; // Center of Missouri
    map.setView(districtCenter, 11);
  }, [students, mapReady, district]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading Student Map...</h2>
        <p>Please wait while we load student location data.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
        <h2>Error Loading Map</h2>
        <p>{error}</p>
      </div>
    );
  }

  const studentsWithCoordinates = students.filter(s => {
    if (!s.coordinates || !s.coordinates.lat || !s.coordinates.lng) return false;

    const lat = parseFloat(s.coordinates.lat);
    const lng = parseFloat(s.coordinates.lng);

    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  });

  return (
    <div style={{
      padding: '1rem',
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto' // Center the content
    }}>
      <h2>Student Residency Map</h2>
      <p style={{ marginBottom: '1rem', color: '#666' }}>
        This map shows student locations from submitted forms ({studentsWithCoordinates.length} students with coordinates).
        District boundaries are overlaid when available. The map is centered to show the district area clearly.
        Pan and zoom to explore markers outside this view. Click on markers to view student details.
      </p>

      <div
        ref={(el) => {
          mapRef.current = el;
          setMapContainerReady(!!el);
        }}
        id="student-map-container"
        style={{
          height: '600px',
          width: '100%',
          maxWidth: '1200px', // Prevent map from becoming too wide
          border: '2px solid #dee2e6',
          borderRadius: '4px',
          backgroundColor: '#f8f9fa'
        }}
      />

      <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
        <p><strong>Map Controls:</strong></p>
        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
          <li>Use mouse wheel or +/- buttons to zoom</li>
          <li>Click and drag to pan the map</li>
          <li>Click on markers to view student information</li>
        </ul>
        <p><em>Note: Student markers cannot be moved from this view. Some markers may be outside the initial view area - use pan and zoom to locate them.</em></p>
      </div>
    </div>
  );
};

export default StudentMap;
